/**
 * Service to manage the user's opt-in to beta experiences.
 *
 * Three-tier pre-release lifecycle:
 *   Alpha  → /reimagined/* routes (experimental, separate)
 *   Beta   → Opt-in via Profile toggle (merged into main templates, production quality)
 *   GA     → Default for everyone, old code removed
 *
 * When the `betaExperience` feature flag is enabled (local/vodka/staging),
 * the Profile page shows a toggle to opt in. Features graduating from alpha
 * to beta check `this.betaExperience.isEnabled` to swap rendering.
 *
 * Per-product toggles allow enabling beta for specific products (e.g. TA only)
 * without affecting other product areas. The global toggle acts as a master
 * switch — enabling it turns on beta for all products.
 *
 * When the flag is OFF (production), the toggle is hidden and beta features
 * are always disabled regardless of localStorage.
 *
 * The same section also hosts visual experiments — today the chamfered-edge
 * treatment — which are gated identically (flag, demo pin) but sit outside
 * the page-level master switch: opting in to beta pages says nothing about
 * wanting a different card edge, and vice versa.
 */
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type FeaturesService from 'tio-common/services/features';
import { CAPABILITY_SOURCE_RANK, type CapabilitySource } from 'tio-common/services/features';
import type DemoModeService from 'tio-employee/services/demo-mode';

const STORAGE_KEY = 'tio-beta-experience';
const PRODUCT_STORAGE_KEY = 'tio-beta-experience-products';
const CHAMFER_STORAGE_KEY = 'tio-beta-chamfered-edges';
/** The `<html>` attribute the edge tokens key off — see tio-theme.css § EDGE: CHAMFER. */
const EDGE_ATTRIBUTE = 'data-edge';
const EDGE_CHAMFER = 'chamfer';

export type BetaProduct = 'ta';

export const BETA_PRODUCTS: BetaProduct[] = ['ta'];

export default class BetaExperienceService extends Service {
  @service declare features: FeaturesService;
  @service declare demoMode: DemoModeService;

  @tracked private _userPreference: boolean | null = null;
  @tracked private _productPreferences: Record<BetaProduct, boolean> = {
    ta: false,
  };
  @tracked private _chamferPreference = false;

  constructor(owner: object) {
    // @ts-expect-error owner type
    super(owner);
    this._loadPreference();
    this._loadProductPreferences();
    this._chamferPreference = localStorage.getItem(CHAMFER_STORAGE_KEY) === 'true';
  }

  /** Whether the feature flag allows the beta toggle to appear */
  get flagEnabled(): boolean {
    return this.features.isEnabled('betaExperience');
  }

  /**
   * Whether beta experiences should render globally.
   * - If feature flag is OFF → always false
   * - If user has a stored preference → use it
   * - Otherwise → default to OFF (user must explicitly opt in)
   */
  get isEnabled(): boolean {
    if (!this.flagEnabled) {
      return false;
    }
    // A demo session always shows the SHIPPED experience: the sales demo
    // exists to show what the app currently is and does (#8022), so a
    // developer's beta opt-in must not swap in beta surfaces mid-demo.
    // (No capability recursion: the beta source only answers its own key.)
    if (this.demoMode.isActive) {
      return false;
    }
    return this._userPreference ?? false;
  }

  /**
   * Whether beta is enabled for a specific product.
   * Returns true if either the global toggle OR the product-specific toggle is on.
   */
  isProductEnabled = (product: BetaProduct): boolean => {
    if (!this.flagEnabled) {
      return false;
    }
    // Same shipped-experience pin as `isEnabled` — see the note there.
    if (this.demoMode.isActive) {
      return false;
    }
    if (this._userPreference) {
      return true;
    }
    return this._productPreferences[product] ?? false;
  };

  /** Toggle beta experience on/off globally */
  toggle = () => {
    const newValue = !this.isEnabled;
    this._userPreference = newValue;
    localStorage.setItem(STORAGE_KEY, String(newValue));
  };

  /** Explicitly set the global preference */
  setEnabled = (value: boolean) => {
    this._userPreference = value;
    localStorage.setItem(STORAGE_KEY, String(value));
  };

  /** Set per-product beta preference */
  setProductEnabled = (product: BetaProduct, value: boolean) => {
    this._productPreferences = {
      ...this._productPreferences,
      [product]: value,
    };
    localStorage.setItem(PRODUCT_STORAGE_KEY, JSON.stringify(this._productPreferences));
  };

  /**
   * Whether the chamfered-edge treatment is on: the flag allows beta toggles,
   * no demo session is active, and the user opted in. Independent of
   * {@link isEnabled} — a visual experiment, not a product surface.
   */
  get isChamferEnabled(): boolean {
    if (!this.flagEnabled || this.demoMode.isActive) {
      return false;
    }
    return this._chamferPreference;
  }

  /** Persist the chamfer opt-in and re-sync the document attribute the CSS reads. */
  setChamferEnabled = (value: boolean) => {
    this._chamferPreference = value;
    localStorage.setItem(CHAMFER_STORAGE_KEY, String(value));
    this.applyEdgeTreatment();
  };

  /**
   * Mirror {@link isChamferEnabled} onto `<html data-edge>`, the only signal the
   * edge tokens respond to. The application route calls this once the feature
   * flags are seeded (the getter reads them), and the setter calls it on every
   * change; a demo session is fixed for the page lifetime — entering one
   * reloads — so those two moments cover every state the getter can take.
   */
  applyEdgeTreatment = () => {
    const html = document.documentElement;
    if (this.isChamferEnabled) {
      html.setAttribute(EDGE_ATTRIBUTE, EDGE_CHAMFER);
    } else {
      html.removeAttribute(EDGE_ATTRIBUTE);
    }
  };

  private _loadPreference() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      this._userPreference = stored === 'true';
    }
  }

  /**
   * Whether the user has explicitly enabled a specific product preference
   * (ignores global toggle). Used for UI binding on disabled switches.
   */
  isProductPreferenceEnabled = (product: BetaProduct): boolean => {
    return this._productPreferences[product] ?? false;
  };

  /**
   * Expose beta opt-in as a {@link CapabilitySource} for the features service
   * (rank below demo). Answers the `betaExperience` capability with the EFFECTIVE
   * state (flag AND user opt-in) — never `flagEnabled` alone, which would force
   * beta on for everyone the moment the flag flips. Abstains on all other keys.
   */
  asCapabilitySource = (): CapabilitySource => {
    return {
      id: 'beta',
      rank: CAPABILITY_SOURCE_RANK.beta,
      resolve: (key) => (key === 'betaExperience' ? this.isEnabled : undefined),
    };
  };

  private _loadProductPreferences() {
    const stored = localStorage.getItem(PRODUCT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        const updated = { ...this._productPreferences };
        for (const product of BETA_PRODUCTS) {
          if (typeof parsed[product] === 'boolean') {
            updated[product] = parsed[product];
          }
        }
        this._productPreferences = updated;
      } catch {
        // Ignore invalid JSON
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'beta-experience': BetaExperienceService;
  }
}
